import {
  domainImageUrl,
  imageProxyUrlFromDocument,
  singleDocumentImageUrl,
  toMediaSrc,
} from './imageProxy';
import { parseVideoLink } from './videoSource';

export const ARTICLE_CONTENT_TYPES = {
  document: 'document',
  photos: 'photos',
  video: 'video',
};

export const ARTICLE_PUBLICATION_STATUSES = {
  draft: 'draft',
  published: 'published',
  archived: 'archived',
};

export const CONTENT_TYPE_LABELS = {
  document: 'Document',
  photos: 'Photos',
  video: 'Vidéo',
};

export const PUBLICATION_STATUS_LABELS = {
  draft: 'Brouillon',
  published: 'Publié',
  archived: 'Archivé',
};

export const DEFAULT_STATUS_FILTER = ['draft', 'published'];

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.webm'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

export const getUrlExtension = (url = '') => {
  const cleanUrl = decodeURIComponent(url).split('?')[0].toLowerCase();
  const dotIndex = cleanUrl.lastIndexOf('.');
  return dotIndex >= 0 ? cleanUrl.slice(dotIndex) : '';
};

export const isVideoUrl = (url = '') => VIDEO_EXTENSIONS.some((ext) => getUrlExtension(url) === ext);

export const isImageUrl = (url = '') => IMAGE_EXTENSIONS.some((ext) => getUrlExtension(url) === ext);

export const getContentTypeLabel = (contentType) =>
  CONTENT_TYPE_LABELS[contentType] || contentType || '—';

export const getMediaLabel = (item) => {
  const photos = getArticlePhotos(item);
  const media = item?.article_media || [];
  if (!media.length && !photos.length) {
    return null;
  }

  if (item.content_type === ARTICLE_CONTENT_TYPES.photos) {
    return photos.length === 1 ? 'Voir la photo' : `Voir les photos (${photos.length})`;
  }

  if (item.content_type === ARTICLE_CONTENT_TYPES.video) {
    return 'Lire la vidéo';
  }

  return 'Ouvrir le document';
};

const resolveMediaImageUrl = (media, { variant = 'default' } = {}) =>
  imageProxyUrlFromDocument(media, { variant }) || toMediaSrc(media?.display_url);

const legacyGalleryMedia = (item) =>
  (item?.article_media || []).filter(
    (media) => media.media_type === 'image' || isImageUrl(media.display_url),
  );

/**
 * Gallery photos, newest model first.
 *
 * Phase 3.5 stores them as `article_photo` (own entity, re-croppable). Items created before
 * the migration still carry their photos as `article_media` rows, so both are normalized
 * to `{ id, title, description, url(size), isLegacy }`.
 */
export const getArticlePhotos = (item) => {
  const photos = item?.article_photo || [];
  if (photos.length) {
    return photos.map((photo) => ({
      id: photo.id,
      title: photo.title || '',
      description: photo.description || '',
      sortOrder: photo.sort_order ?? 0,
      image: photo.article_photo_image?.[0] || null,
      isLegacy: false,
      url: (size = 'default') =>
        domainImageUrl(photo, { imageField: 'article_photo_image', size }),
    }));
  }

  return legacyGalleryMedia(item).map((media) => ({
    id: media.id,
    title: media.name || '',
    description: '',
    sortOrder: media.sort_order ?? 0,
    image: null,
    isLegacy: true,
    url: (size = 'default') =>
      resolveMediaImageUrl(media, { variant: size === 'small' ? 'small' : 'default' }),
  }));
};

export const hasArticleMedia = (item) =>
  Boolean(item?.article_media?.length) || Boolean(item?.article_photo?.length);

export const getGalleryUrls = (item) =>
  getArticlePhotos(item)
    .map((photo) => photo.url('default'))
    .filter(Boolean);

export const getGalleryThumbUrls = (item) =>
  getArticlePhotos(item)
    .map((photo) => photo.url('small'))
    .filter(Boolean);

export const getPrimaryMediaUrl = (item) => {
  const [photo] = getArticlePhotos(item);
  if (photo) {
    return photo.url('small');
  }
  const media = item?.article_media?.[0];
  if (!media) return null;
  return resolveMediaImageUrl(media, { variant: 'small' });
};

export const getVideoUrl = (item) => {
  const media = item?.article_media?.find(
    (entry) => entry.media_type === 'video' || isVideoUrl(entry.display_url),
  );
  return media ? toMediaSrc(media.display_url) : null;
};

/** Shape expected by the shared VideoPlayerDialog (upload or online link). */
export const getArticleVideoPlayback = (item) => {
  const media = item?.article_media?.find(
    (entry) => entry.media_type === 'video' || isVideoUrl(entry.display_url) || parseVideoLink(entry.display_url),
  );
  if (!media?.display_url) {
    return null;
  }

  const rawUrl = media.display_url;
  const isHostedFile =
    rawUrl.includes('amazonaws.com') || rawUrl.includes('/image-proxy?') || Boolean(media.key);

  if (!isHostedFile) {
    const parsed = parseVideoLink(rawUrl);
    if (parsed) {
      return {
        title: item.title,
        source_kind: 'link',
        provider: parsed.provider,
        external_url: parsed.externalUrl,
        embed_url: parsed.embedUrl,
      };
    }
  }

  return {
    title: item.title,
    source_kind: 'upload',
    display_url: toMediaSrc(rawUrl) || rawUrl,
  };
};

export const getArticleMediaDocumentUrl = (media) =>
  singleDocumentImageUrl(media, { variant: 'default' }) || toMediaSrc(media?.display_url);

export const getPdfUrl = (item) => {
  const media = item?.article_media?.find(
    (entry) => entry.media_type === 'pdf' || (!isVideoUrl(entry.display_url) && !isImageUrl(entry.display_url)),
  );
  if (!media) return null;
  // Signed / proxy URL when the cache is warm. Otherwise pass key or legacy
  // display_url — usePdfPreview.resolveSignedMediaSrc signs before pdf.js runs.
  return getArticleMediaDocumentUrl(media) || media.key || media.display_url || null;
};
